import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
	Edit,
	Image as ImageIcon,
	Layers,
	Link2,
	Sparkles,
	Store,
	Tag,
} from "lucide-react"
import {
	ENTITY_PAGE_CLASS,
	EntityDetailSkeleton,
	EntityDetailSplit,
	PriceAvailabilityPanel,
	SUMMARY_LINK_CLASS,
	SummaryGroup,
	SummaryLinkContent,
	SummaryPanel,
} from "@/components/catalogue-entity/entity-detail-layout"
import {
	DetailPanel,
	DetailPanelSection,
	PageHeader,
} from "@/components/layout"
import { CensoredToggle } from "@/components/products/censored-toggle"
import {
	GameplayFields,
	MarketplaceIdsSection,
	ProductDetailsSection,
	ProductImage,
} from "@/components/products/product-detail-sections"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useFieldGroups } from "@/queries/config/use-field-groups"
import { useFilterOptions } from "@/queries/metadata/use-filter-options"
import { useStagingProduct } from "@/queries/products/use-staging-product"
import type { SingleProductData } from "@/types/products.types"
import { formatGameName } from "@/utils/game.helpers"

const TABS = ["details", "gameplay", "marketplace"] as const
type ProductTab = (typeof TABS)[number]

export const Route = createFileRoute("/products/singles/$id/")({
	component: ProductDetailPage,
	validateSearch: (search: Record<string, unknown>) => ({
		...createTabSearch(TABS)(search),
		...(typeof search.gameSlug === "string"
			? { gameSlug: search.gameSlug }
			: {}),
		...(typeof search.expansionSlug === "string"
			? { expansionSlug: search.expansionSlug }
			: {}),
	}),
})

function ProductDetailPage() {
	const { id } = Route.useParams()
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const { product, isLoading, error } = useStagingProduct({
		productId: id,
		gameSlug: searchParams.gameSlug,
	})

	const activeTab = searchParams.tab ?? "details"
	const setActiveTab = (tab: string) => {
		navigate({
			to: "/products/singles/$id",
			params: { id },
			search: (prev) => ({
				...prev,
				tab: tab === "details" ? undefined : (tab as ProductTab),
			}),
			replace: true,
		})
	}

	const { fieldGroups, isLoading: isLoadingFields } = useFieldGroups({
		gameSlug: product?.gameSlug || "",
		entityType: "product",
		enabled: !!product?.gameSlug,
	})

	const { data: expansionOptions = [] } = useFilterOptions({
		entityType: "product",
		field: "expansionSlug",
		gameSlug: product?.gameSlug,
		enabled: !!product?.gameSlug,
	})

	if (isLoading) {
		return <EntityDetailSkeleton />
	}

	if (error || !product) {
		return (
			<div className="p-4 sm:p-6">
				<PageHeader
					title="Product not found"
					description={
						error?.message || `The product with ID ${id} could not be found.`
					}
				/>
			</div>
		)
	}

	const productData = product.data as SingleProductData
	const gameSlug = product.gameSlug
	const gameName = formatGameName(gameSlug)

	const expansionSlug = productData.expansion
	const expansionName =
		expansionOptions.find((opt) => opt.value === expansionSlug)?.label ||
		expansionSlug

	return (
		<div className={ENTITY_PAGE_CLASS}>
			<PageHeader
				title={productData.name}
				description={
					<span className="flex flex-wrap items-center gap-x-2">
						<span className="font-mono text-foreground">
							{product.entitySlug}
						</span>
						<span className="text-meta">·</span>
						<span>{gameName}</span>
						{expansionName && (
							<>
								<span className="text-meta">·</span>
								<span>{expansionName}</span>
							</>
						)}
						{productData.printNumber && (
							<>
								<span className="text-meta">·</span>
								<span className="tabular-nums">#{productData.printNumber}</span>
							</>
						)}
					</span>
				}
				actions={
					<Button asChild>
						<Link
							to="/products/singles/$id/manage"
							params={{ id }}
							search={{
								gameSlug: product?.gameSlug,
								expansionSlug: productData.expansion,
							}}
						>
							<Edit />
							Edit
						</Link>
					</Button>
				}
			/>

			<EntityDetailSplit
				aside={
					<>
						<SummaryPanel title="Image" icon={ImageIcon}>
							<SummaryGroup className="gap-2">
								<ProductImage
									image={productData.image}
									name={productData.name || ""}
								/>
								<CensoredToggle
									gameSlug={gameSlug}
									entitySlug={product.entitySlug ?? ""}
									censored={productData.censored === true}
								/>
							</SummaryGroup>
						</SummaryPanel>

						<SummaryPanel title="Related content" icon={Link2}>
							<SummaryGroup>
								<Button asChild variant="ghost" className={SUMMARY_LINK_CLASS}>
									<Link to="/games/$id" params={{ id: gameSlug }}>
										<SummaryLinkContent
											icon={<Layers />}
											label="View game details"
											value={gameName}
										/>
									</Link>
								</Button>
								{expansionSlug && (
									<Button
										asChild
										variant="ghost"
										className={SUMMARY_LINK_CLASS}
									>
										<Link to="/catalogue/games/$gameSlug" params={{ gameSlug }}>
											<SummaryLinkContent
												icon={<Layers />}
												label="View expansion"
												value={expansionName}
											/>
										</Link>
									</Button>
								)}
							</SummaryGroup>
						</SummaryPanel>
					</>
				}
			>
				<div className="flex flex-col gap-4">
					<Tabs
						value={activeTab}
						onValueChange={setActiveTab}
						className="gap-6"
					>
						<TabsList variant="line">
							<TabsTrigger value="details">Details</TabsTrigger>
							<TabsTrigger value="gameplay">Gameplay</TabsTrigger>
							<TabsTrigger value="marketplace">Marketplace</TabsTrigger>
						</TabsList>

						<TabsContent value="details">
							<DetailPanel title="Product details" icon={Tag}>
								<DetailPanelSection>
									<ProductDetailsSection
										product={product}
										productData={productData}
										expansionName={expansionName || ""}
										gameSlug={gameSlug}
									/>
								</DetailPanelSection>
							</DetailPanel>
						</TabsContent>

						<TabsContent value="gameplay">
							<DetailPanel title="Gameplay information" icon={Sparkles}>
								<DetailPanelSection>
									{isLoadingFields ? (
										<div className="flex flex-col gap-2">
											<Skeleton className="h-5 w-[240px]" />
											<Skeleton className="h-5 w-[200px]" />
											<Skeleton className="h-5 w-[260px]" />
										</div>
									) : (
										(GameplayFields({ data: productData, fieldGroups }) ?? (
											<p className="text-meta">No gameplay information</p>
										))
									)}
								</DetailPanelSection>
							</DetailPanel>
						</TabsContent>

						<TabsContent value="marketplace">
							<DetailPanel title="Marketplace IDs" icon={Store}>
								<DetailPanelSection>
									<MarketplaceIdsSection
										externalIds={productData.externalIds}
										gameSlug={gameSlug}
									/>
								</DetailPanelSection>
							</DetailPanel>
						</TabsContent>
					</Tabs>

					<PriceAvailabilityPanel />
				</div>
			</EntityDetailSplit>
		</div>
	)
}
